<template>
<div class="d3-timeline-container">
    <h2 v-if="title">{{ title }}</h2>
    <svg ref="timelineSvg" :width="width" :height="height"></svg>
    <div v-if="selectedEvent" class="event-details">
        <h3>{{ selectedEvent.title }}</h3>
        <p>{{ selectedEvent.date }}</p>
        <p>{{ selectedEvent.description }}</p>
    </div>
</div>
</template>
  
  <script>
  import * as d3 from 'd3';
  
  export default {
    props: {
        events: {
            type: Array,
            required: true,
            validator: events => events.every(e => e.date && e.title)
        },
        width: {
            type: Number,
            default: 800
        },
        height: {
            type: Number,
            default: 500
        },
        title: {
            type: String,
            default: 'roadmap'
        }
    },
    data() {
      return {
        selectedEvent: null,
        margin: { top: 50, right: 50, bottom: 50, left: 50 }
      };
    },
    computed: {
      innerWidth() {
        return this.width - this.margin.left - this.margin.right;
      },
      innerHeight() {
        return this.height - this.margin.top - this.margin.bottom;
      }
    },
    mounted() {
      this.renderTimeline();
    },
    watch: {
      events: {
        handler() {
          this.renderTimeline();
        },
        deep: true
      }
    },
    methods: {
      renderTimeline() {
        // Clear previous rendering
        d3.select(this.$refs.timelineSvg).selectAll("*").remove();
  
        // Parse dates
        const parseTime = d3.timeParse("%Y-%m-%d");
        const data = this.events.map(d => ({
          ...d,
          parsedDate: parseTime(d.date)
        })).sort((a, b) => a.parsedDate - b.parsedDate);
  
        // Create scales
        const x = d3.scaleTime()
          .domain(d3.extent(data, d => d.parsedDate))
          .range([0, this.innerWidth]);
  
        // const y = d3.scalePoint()
        //   .domain(data.map((d, i) => i))
        //   .range([0, this.innerHeight])
        //   .padding(0.5);

        
  
        // Create SVG group
        const svg = d3.select(this.$refs.timelineSvg)
          .attr("viewBox", `0 0 ${this.width} ${this.height}`)
          .append("g")
          .attr("transform", `translate(${this.margin.left},${this.margin.top})`);
  
        // Add timeline axis
        svg.append("g")
          .attr("class", "axis axis--x")
          .attr("transform", `translate(0,${this.innerHeight / 2})`)
          .call(d3.axisBottom(x)
            .tickFormat(d3.timeFormat("%b %Y"))
            .ticks(6)
          );
  
        // Draw timeline line
        svg.append("line")
          .attr("class", "timeline-line")
          .attr("x1", 0)
          .attr("y1", this.innerHeight / 2)
          .attr("x2", this.innerWidth)
          .attr("y2", this.innerHeight / 2)
          .attr("stroke", "#42b983")
          .attr("stroke-width", 3);
  
        // Add event markers
        const markers = svg.selectAll(".event-marker")
          .data(data)
          .enter()
          .append("g")
          .attr("class", "event-marker")
          .attr("transform", d => `translate(${x(d.parsedDate)},${this.innerHeight / 2})`)
          .on("mouseover", (event, d) => {
            this.selectedEvent = d;
            d3.select(event.currentTarget).select("circle")
              .transition()
              .duration(200)
              .attr("r", 10)
              .attr("fill", "#ff7e5f");
          })
          .on("mouseout", (event, /*d*/) => {
            d3.select(event.currentTarget).select("circle")
              .transition()
              .duration(200)
              .attr("r", 8)
              .attr("fill", "#42b983");
          });
  
        // Add circles for each event
        markers.append("circle")
          .attr("r", 8)
          .attr("fill", "#42b983")
          .attr("stroke", "#fff")
          .attr("stroke-width", 2);
  
        // Add text labels
        markers.append("text")
          .attr("dy", d => (d.id % 2 === 0 ? "4.5em" : "-4.5em"))
          .attr("text-anchor", "middle")
          .text(d => d.title)
          .attr("fill", "#333")
          .style("font-size", "12px")
          .style("font-weight", "bold");
  
        // Add date labels
        markers.append("text")
          .attr("dy", d => (d.id % 2 === 0 ? "3.5em" : "-3.5em"))
          .attr("text-anchor", "middle")
          .text(d => d3.timeFormat("%b %d, %Y")(d.parsedDate))
          .attr("fill", "#666")
          .style("font-size", "10px");
  
        // Add connecting lines
        markers.append("line")
          .attr("y1", d => (d.id % 2 === 0 ? 15 : -15))
          .attr("y2", d => (d.id % 2 === 0 ? 30 : -30))
          .attr("stroke", "#ccc")
          .attr("stroke-width", 1)
          .attr("stroke-dasharray", "2,2");

        markers.transition()
            .duration(1000)
            .attr("transform", d => `translate(${x(d.parsedDate)},${this.innerHeight / 2})`);

        // Replace circles with custom icons
        markers.append("path")
            .attr("d", d3.symbol().type(d3.symbolCircle).size(100))
            .attr("fill", d => colorScale(d.category));

        // Replace circles with custom icons
        markers.append("path")
        .attr("d", d3.symbol().type(d3.symbolCircle).size(100))
        .attr("fill", d => colorScale(d.category));
          
        // Use D3 color schemes
        const colorScale = d3.scaleOrdinal(d3.schemeTableau10);

        // Add zoom behavior
        // const zoom = d3.zoom()
        // .scaleExtent([0.5, 5])
        // .on("zoom", (event) => {
        //     svg.attr("transform", event.transform);
        // });

        //d3.select(this.$refs.timelineSvg).call(zoom);
        
    }

    }
  };
  </script>
  
  <style scoped>
  .d3-timeline-container {
    font-family: 'Arial', sans-serif;
    background: #f9f9f9;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  
  .event-details {
    margin-top: 20px;
    padding: 15px;
    background: white;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  svg {
    display: block;
    margin: 0 auto;
  }
  </style>